import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';

class PushNotificationService {
  constructor() {
    this.isInitialized = false;
  }

  async initialize() {
    // Only initialize on native platforms
    if (!Capacitor.isNativePlatform()) {
      console.log('Push notifications only available on native platforms');
      return;
    }

    if (this.isInitialized) {
      return;
    }

    try {
      // Request permission
      const permission = await PushNotifications.requestPermissions();

      if (permission.receive === 'granted') {
        // Register for push notifications
        await PushNotifications.register();
        this.setupListeners();
        this.isInitialized = true;
        console.log('Push notifications initialized successfully');
      } else {
        console.log('Push notification permission denied');
      }
    } catch (error) {
      console.error('Error initializing push notifications:', error);
    }
  }

  setupListeners() {
    // On successful registration
    PushNotifications.addListener('registration', (token) => {
      console.log('Push registration success, token:', token.value);
      // Send token to your backend
      this.sendTokenToBackend(token.value);
    });

    // On registration error
    PushNotifications.addListener('registrationError', (error) => {
      console.error('Push registration error:', error);
    });

    // When a notification is received (app in foreground)
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push notification received:', notification);
      // Handle foreground notification
      this.handleForegroundNotification(notification);
    });

    // When user taps on notification
    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      console.log('Push notification action performed:', notification);
      // Handle notification tap
      this.handleNotificationTap(notification);
    });
  }

  async sendTokenToBackend(token) {
    try {
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        console.log('No auth token found, skipping token registration');
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/notifications/register-device`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          token: token,
          platform: Capacitor.getPlatform()
        })
      });

      if (response.ok) {
        console.log('Device token registered successfully');
      } else {
        console.error('Failed to register device token');
      }
    } catch (error) {
      console.error('Error sending token to backend:', error);
    }
  }

  handleForegroundNotification(notification) {
    // Show a local notification or in-app alert
    console.log('Foreground notification:', notification.title, notification.body);

    // You could show a toast/snackbar here
    // For now, we'll just log it
  }

  handleNotificationTap(notification) {
    const data = notification.notification.data;
    console.log('Notification tapped with data:', data);

    // Handle navigation based on notification data
    if (data.type === 'goal_reminder') {
      // Navigate to goals page
      window.location.href = '/goals';
    } else if (data.type === 'milestone_due') {
      // Navigate to specific goal
      window.location.href = `/goals/${data.goalId}`;
    }
  }

  async checkPermissions() {
    if (!Capacitor.isNativePlatform()) {
      return { receive: 'prompt' };
    }

    return await PushNotifications.checkPermissions();
  }

  async requestPermissions() {
    if (!Capacitor.isNativePlatform()) {
      return { receive: 'denied' };
    }

    return await PushNotifications.requestPermissions();
  }

  async getDeliveredNotifications() {
    if (!Capacitor.isNativePlatform()) {
      return [];
    }

    const result = await PushNotifications.getDeliveredNotifications();
    return result.notifications;
  }

  async removeDeliveredNotifications(notifications) {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    await PushNotifications.removeDeliveredNotifications({
      notifications: notifications
    });
  }

  async removeAllDeliveredNotifications() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    await PushNotifications.removeAllDeliveredNotifications();
  }
}

// Export singleton instance
export default new PushNotificationService();
